import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider, useWatch, useFormState } from 'react-hook-form';
import { Grid } from '@material-ui/core';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSTextField, RMSSelect } from '../../../shared/components/form-controls';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import RMSRecordHistoryButton from '../../../shared/components/modules/RMSRecordHistoryButton';
import PartyKindLookup from './PartyKindLookup';
import CreateFormResolver from '../../../shared/utility/createFormResolver';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import addressPartySchema from '../../schema/addressPartySchema';
import useUserDictionaryOptions from '../../hooks/useUserDictionaryOptions';
import { saveAddressParty } from '../../services/userService';
import { DICT_SELECT_PROPS, DEFAULT_VALUES } from '../../constants/addressPartyFormConstants';

const AddressPartyDialog = ({ open, onClose, ptsAddressID, party = null, onSaved }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.userData.user);
  const [submitting, setSubmitting] = useState(false);
  const isEditMode = Boolean(party?.ROWGUID);
  const { addressPartyRelationshipOptions } = useUserDictionaryOptions();

  const methods = useForm({
    resolver: CreateFormResolver(addressPartySchema),
    mode: 'onSubmit',
    defaultValues: isEditMode
      ? {
          PartyType: party.PartyType || 'Person',
          ptsPartyID: party.ptsPartyID ?? null,
          PartyRelationship: party.Relationship || '',
          PriorityOrder: party.PriorityOrder ?? 0,
          AdvisoryText: party.AdvisoryText || '',
        }
      : DEFAULT_VALUES,
  });

  const { isDirty } = useFormState({ control: methods.control });
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const ptsPartyID = useWatch({ control: methods.control, name: 'ptsPartyID' });

  const onSubmit = useCallback(
    async (data) => {
      const payload = {
        ptsAddressID,
        ptsPartyID: data.ptsPartyID,
        PartyType: data.PartyType,
        AddressPartyRelationship: data.PartyRelationship,
        PriorityOrder: data.PriorityOrder != null ? parseInt(data.PriorityOrder, 10) : null,
        AdvisoryText: data.AdvisoryText || null,
        ROWGUID: isEditMode ? party.ROWGUID : undefined,
        CreatedBy: currentUser?.Username,
        UpdatedBy: currentUser?.Username,
      };

      await executeApiAction({
        setLoading: setSubmitting,
        dispatch,
        action: () => saveAddressParty(payload),
        onSuccess: () => {
          dispatch(
            notify(
              isEditMode ? 'Party updated successfully' : 'Party added successfully',
              'success'
            )
          );
          onSaved?.();
        },
      });
    },
    [isEditMode, party, ptsAddressID, currentUser, dispatch, onSaved]
  );

  const handleSave = methods.handleSubmit(onSubmit);

  const requestClose = useCallback(() => {
    if (isDirty) {
      requestConfirm(
        'You have unsaved changes on this party. Are you sure you want to close without saving?',
        () => onClose?.(),
        'Unsaved Changes'
      );
      return;
    }

    onClose?.();
  }, [isDirty, requestConfirm, onClose]);

  return (
    <>
      <RMSCommonDialog
        open={open}
        title={isEditMode ? 'Edit Party' : 'Add Party'}
        onClose={requestClose}
        onSave={handleSave}
        loading={submitting}
        saveDisabled={submitting}
        maxWidth="sm"
        fullWidth
        closeOnBackdropClick={false}
        saveButton={{
          show: true,
          type: 'simple',
          label: 'Save',
          variant: 'contained',
          color: 'primary',
        }}
        cancelButton={{
          show: true,
          type: 'simple',
          label: 'Close',
          variant: 'outlined',
          color: 'secondary',
        }}
        leftActions={
          isEditMode && (
            <RMSRecordHistoryButton
              disabled={!party?.Created}
              created={party?.Created}
              createdBy={party?.CreatedBy}
              updated={party?.Updated}
              updatedBy={party?.UpdatedBy}
            />
          )
        }>
        <FormProvider {...methods}>
          <Grid container spacing={2}>
            <PartyKindLookup
              typeFieldName="PartyType"
              idFieldName="ptsPartyID"
              typeLabel="Party Type"
              idLabel="Party Search"
              idValue={ptsPartyID}
            />
            <Grid item xs={6}>
              <RMSSelect
                name="PartyRelationship"
                label="Relationship"
                options={addressPartyRelationshipOptions}
                {...DICT_SELECT_PROPS}
              />
            </Grid>
            <Grid item xs={6}>
              <RMSTextField
                name="PriorityOrder"
                label="Priority Order"
                type="number"
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12}>
              <RMSTextField name="AdvisoryText" label="Advisory Text" multiline rows={4} />
            </Grid>
          </Grid>
        </FormProvider>
      </RMSCommonDialog>

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </>
  );
};

export default AddressPartyDialog;
